import { useRef, useCallback } from 'react';
import type { TabOption } from '@/types/navigation';
import styles from './Tabs.module.css';

interface TabsProps<V extends string = string> {
  options: readonly TabOption<V>[];
  value: V;
  onChange: (v: V) => void;
  variant?: 'tabs' | 'segmented';
  ariaLabel: string;
}

export function Tabs<V extends string = string>({
  options,
  value,
  onChange,
  variant = 'tabs',
  ariaLabel,
}: TabsProps<V>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, idx: number) => {
      let nextIdx: number | null = null;

      if (e.key === 'ArrowRight') {
        nextIdx = (idx + 1) % options.length;
      } else if (e.key === 'ArrowLeft') {
        nextIdx = (idx - 1 + options.length) % options.length;
      } else if (e.key === 'Home') {
        nextIdx = 0;
      } else if (e.key === 'End') {
        nextIdx = options.length - 1;
      }

      if (nextIdx !== null) {
        e.preventDefault();
        tabRefs.current[nextIdx]?.focus();
        onChange(options[nextIdx].value);
      }
    },
    [options, onChange],
  );

  const cls = variant === 'segmented' ? styles.segmented : styles.tabs;

  return (
    <div className={cls} role="tablist" aria-label={ariaLabel} data-testid="tabs">
      {options.map((opt, i) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`${styles.tab} ${selected ? styles.selected : ''}`}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
